import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import { fromStore } from 'angular/app/core/store';
import { authActions } from 'angular/app/core/store/auth';
import { User } from '../../../core/models/entities/user';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { take } from 'rxjs/operators';
import { VendorsService } from '../../../core/services/vendors.service';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { ApiError } from '../../../core/models/common/api-error';

@Component({
  selector: 'app-test-mode-page',
  template: `
    <app-page-header title="Mode test"></app-page-header>
    
    <app-spacer size="6"></app-spacer>
    
    <div>
      <app-fieldset>
        <div title>Mode test</div>
        <div description>
          <p
            >En mode test, le processus de paiement en ligne utilise un compte
            fictif, excluant toute transaction réelle d'argent.</p
            >
    
            <p class="mt-4"
              >Ce mode vous permet de simuler des réservations et d’évaluer
              l’interface finale, reproduisant ainsi l’expérience utilisateur.</p
              >
    
              <p class="mt-4"
                >Notez que
                <strong
                  >seules les données affichées dans les sections "Réservations" et
                  "Facturation" sont spécifiques au mode test</strong
                  >. Les autres données, telles que celles liées aux salles, restent
                  inchangées et s'appliquent dans les deux modes.</p
                  >
    
                  <p class="mt-4"
                    >Lorsque vous désactivez le mode test pour passer en mode
                    "production", les données de test sont isolées et remplacées par un
                    ensemble distinct, garantissant une séparation claire entre les
                    données de test et celles de votre activité réelle.</p
                    >
                  </div>
    
                  @if (form) {
                    <form class="bg-light rounded-lg p-8">
                      <div class="flex items-center">
                        <app-toggle
                          [defaultValue]="form.get('test_mode').value"
                          (isEnabled)="form.get('test_mode').patchValue($event)"
                          title="Mode test pour tous les utilisateurs"
                        ></app-toggle>
                        @if (sendingData) {
                          <app-loader class="ml-2" size="sm"></app-loader>
                        }
                      </div>
                    </form>
                  }
                </app-fieldset>
              </div>
    `,
  standalone: false,
})
export class TestModePageComponent implements OnInit, OnDestroy {
  loggedUser$: Observable<User> = this.store.select(fromStore.selectAuthUser);
  form: UntypedFormGroup;
  sendingData = false;
  onDestroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private vendorsService: VendorsService,
    private formValidationService: FormValidationService,
  ) {}

  ngOnInit() {
    this.loggedUser$.pipe(take(1)).subscribe((user) => {
      this.form = new UntypedFormGroup({
        test_mode: new UntypedFormControl(user.vendor.test_mode),
      });

      this.form.valueChanges
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((value) => {
          this.submit();
        });
    });
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  async submit() {
    this.loggedUser$.pipe(take(1)).subscribe(async (user) => {
      if (!this.formValidationService.validate(this.form)) return;
      this.sendingData = true;
      try {
        const vendor = await this.vendorsService.updateTestMode(
          this.form.value,
        );
        this.store.dispatch(
          authActions.findCurrentUserSuccess({ user: { ...user, vendor } }),
        );
      } catch (exception) {
        this.formValidationService.displayApiValidationErrors(
          this.form,
          exception as ApiError,
        );
      }
      this.sendingData = false;
    });
  }
}
